import { ReactNode, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LayoutDashboard, Users, CreditCard, Bell, Settings, LogOut, Menu, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { apiRequest } from "@/lib/queryClient";
import { ParentSupportDialog } from "@/components/parent/ParentSupportDialog";
import { capturePostHogEvent, capturePostHogPageview, identifyPostHogUser } from "@/lib/posthog";
import logoUrl from "@assets/edufocal-logo-min-50292a59_1758905778671.png";

interface ParentMe {
  id: string;
  email: string | null;
  phone: string | null;
  firstName: string | null;
  lastName: string | null;
  childCount: number;
}

const navItems = [
  { href: "/parent", label: "Overview", icon: LayoutDashboard, exact: true },
  { href: "/parent/children", label: "Children", icon: Users },
  { href: "/parent/billing", label: "Billing", icon: CreditCard },
  { href: "/parent/notifications", label: "Notifications", icon: Bell },
  { href: "/parent/settings", label: "Settings", icon: Settings },
];

function NavLinks({ pathname, onNavigate, unreadCount }: { pathname: string; onNavigate?: () => void; unreadCount: number }) {
  return (
    <nav className="space-y-1">
      {navItems.map((item) => {
        const active = item.exact ? pathname === item.href : pathname === item.href || pathname.startsWith(item.href + "/");
        const showBadge = item.label === "Notifications" && unreadCount > 0;
        return (
          <Link
            key={item.href}
            href={item.href}
            onClick={onNavigate}
            className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
            data-testid={`parent-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <item.icon className="h-4 w-4" />
            <span className="flex-1">{item.label}</span>
            {showBadge && (
              <Badge variant={active ? "secondary" : "default"} className="text-[10px] h-4 px-1.5">
                {unreadCount}
              </Badge>
            )}
          </Link>
        );
      })}
    </nav>
  );
}

export function ParentLayout({ children }: { children: ReactNode }) {
  const [pathname, setLocation] = useLocation();
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery<ParentMe>({
    queryKey: ["/api/parent/me"],
  });

  const { data: notif } = useQuery<{ unreadCount: number }>({
    queryKey: ["/api/parent/notifications"],
    enabled: !!data,
  });
  const unreadCount = notif?.unreadCount ?? 0;

  useEffect(() => {
    if (isError) setLocation("/parent/login");
  }, [isError, setLocation]);

  // Identify the parent in PostHog and capture parent-surface pageviews.
  useEffect(() => {
    if (data?.id) {
      identifyPostHogUser(`parent:${data.id}`, {
        surface: "parent_dashboard",
        childCount: data.childCount,
        hasEmail: !!data.email,
        hasPhone: !!data.phone,
      });
    }
  }, [data?.id, data?.childCount, data?.email, data?.phone]);

  useEffect(() => {
    if (!data?.id) return;
    capturePostHogPageview();
    capturePostHogEvent("parent_page_view", { path: pathname });
  }, [pathname, data?.id]);

  const logout = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/parent/auth/logout");
    },
    onSuccess: () => {
      capturePostHogEvent("parent_logout");
      qc.clear();
      setLocation("/parent/login");
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  const displayName = [data.firstName, data.lastName].filter(Boolean).join(" ") || data.email || data.phone || "Parent";

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b sticky top-0 z-30 bg-background">
        <div className="flex items-center justify-between gap-4 px-4 md:px-6 h-14">
          <div className="flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="parent-mobile-menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72">
                <SheetTitle className="flex items-center gap-2">
                  <img src={logoUrl} alt="EduFocal" className="h-7 w-auto" />
                  Parent Dashboard
                </SheetTitle>
                <div className="mt-6">
                  <NavLinks pathname={pathname} unreadCount={unreadCount} />
                </div>
              </SheetContent>
            </Sheet>
            <Link href="/parent" className="flex items-center gap-2">
              <img src={logoUrl} alt="EduFocal" className="h-7 w-auto" />
              <span className="hidden sm:inline font-semibold">Parent Dashboard</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:block text-right">
              <p className="text-sm font-medium" data-testid="parent-display-name">{displayName}</p>
              <p className="text-xs text-muted-foreground">{data.childCount} {data.childCount === 1 ? "child" : "children"}</p>
            </div>
            <ParentSupportDialog />
            <Button variant="outline" size="sm" onClick={() => logout.mutate()} disabled={logout.isPending} data-testid="parent-logout">
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        <aside className="hidden md:block w-60 shrink-0 border-r min-h-[calc(100vh-3.5rem)] p-4">
          <NavLinks pathname={pathname} unreadCount={unreadCount} />
        </aside>
        <main className="flex-1 p-4 md:p-6 lg:p-8 max-w-5xl">
          {children}
        </main>
      </div>
    </div>
  );
}
